import { Column, Entity, Index, JoinColumn, ManyToOne } from 'typeorm';
import { BaseEntity } from './base.entity';
import { CatalogValueEntity } from './catalog-value.entity';
import { ClientAddressEntity } from './client-address.entity';
import { WarehouseEntity } from './warehouse.entity';

/**
 * Operational status of a merchandiser — merchandiser-owned, NOT a status
 * borrowed from the location it used to be. Merchandisers previously rode on
 * `LocationStatus` (where maintenance/offline were really equipment statuses
 * being abused on the location row). The merchandiser now carries its own
 * status; the location keeps only its active/inactive meaning. Mirrors
 * VehicleStatus (PD-13767).
 */
export enum MerchandiserStatus {
	ACTIVE = 'active',
	INACTIVE = 'inactive',
	MAINTENANCE = 'maintenance',
	OFFLINE = 'offline',
}

/** Who owns the physical merchandiser unit (PD-14339). */
export enum MerchandiserOwnership {
	COMPANY_OWNED = 'company_owned',
	CUSTOMER_OWNED = 'customer_owned',
}

/**
 * A merchandiser — the freezer/cooler unit placed at a customer that holds
 * bagged ice.
 *
 * Previously a "merchandiser" was modeled only as a `LocationEntity` row with
 * `type='merchandiser'`, which forced the location row to do two jobs at once:
 * it WAS the merchandiser's identity AND the inventory bin. PD-13921 makes the
 * merchandiser a real entity that OWNS one or more child locations (bins) via
 * `location.fk_merchandiser__id` — the same move PD-13767 made for vehicles.
 *
 * Unlike a vehicle, a merchandiser has NO single "primary" location: a unit
 * that holds two products (e.g. a 20 lb bag bin + a 7 lb bag bin) becomes two
 * child locations, and inventory/capacity is TOTALED across all of them
 * (PD-13922). So there is no `fk_location__id_primary` here.
 *
 * Placement lives on the merchandiser (the asset), not the bin: a merchandiser
 * sits at EITHER a customer address (`fk_client_address__id`) OR a servicing
 * warehouse (`fk_warehouse__id`) — both nullable. Once placement is on the
 * merchandiser, `location.fk_client_address__id` becomes redundant and is
 * dropped in PD-13925.
 */
@Entity('merchandiser')
export class MerchandiserEntity extends BaseEntity {
	@Column('varchar', { name: 'name', length: 255 })
	public name!: string;

	@Column('enum', {
		name: 'status',
		enum: MerchandiserStatus,
		default: MerchandiserStatus.ACTIVE,
	})
	public status!: MerchandiserStatus;

	/** Company Owned vs Customer Owned (PD-14339). Nullable — unset by default. */
	@Column('enum', {
		name: 'ownership',
		enum: MerchandiserOwnership,
		nullable: true,
	})
	public ownership!: MerchandiserOwnership | null;

	@Column('varchar', { name: 'description', nullable: true, length: 255 })
	public description!: string | null;

	/**
	 * Definition info (PD-13923) — like a vehicle's make/model/year. Today a
	 * merchandiser is an ice chest, but the model is meant to cover other placed
	 * units later (e.g. a customer's warehouse part-vending machines), so the
	 * record carries basic manufacturer/model/year.
	 */
	@Column('smallint', { name: 'year', unsigned: true, nullable: true })
	public year!: number | null;

	/**
	 * Unit serial number (PD-14226). Free text — unique per physical unit,
	 * so not a catalog.
	 */
	/**
	 * PD-15486 — unique when present. Optional by design: units are regularly
	 * received before their serial plate is available. MySQL treats NULLs as
	 * distinct in a unique index, so any number of units may carry none;
	 * blank strings are normalised to NULL on save because those WOULD
	 * collide. Existing databases get the index from
	 * 1786832767582-AddMerchandiserSerialUniqueIndex.
	 */
	@Index('merchandiser_serial_number_unique', { unique: true })
	@Column('varchar', { name: 'serialNumber', nullable: true, length: 128 })
	public serialNumber!: string | null;

	/**
	 * Managed-catalog manufacturer (catalog_value, type
	 * merchandiser_manufacturer). PD-14226 — supersedes the free-text
	 * `manufacturer` column.
	 */
	@Column('int', {
		name: 'fk_catalog_value__id_manufacturer',
		unsigned: true,
		nullable: true,
	})
	public manufacturerId!: number | null;

	/**
	 * Managed-catalog model (catalog_value, type merchandiser_model). Its
	 * catalog value's parent is the chosen manufacturer. PD-14226.
	 */
	@Column('int', {
		name: 'fk_catalog_value__id_model',
		unsigned: true,
		nullable: true,
	})
	public modelId!: number | null;

	@ManyToOne(() => CatalogValueEntity, { nullable: true })
	@JoinColumn({ name: 'fk_catalog_value__id_manufacturer' })
	public manufacturerCatalog!: CatalogValueEntity | null;

	@ManyToOne(() => CatalogValueEntity, { nullable: true })
	@JoinColumn({ name: 'fk_catalog_value__id_model' })
	public modelCatalog!: CatalogValueEntity | null;

	/**
	 * The customer placement — the client address this merchandiser sits at.
	 * Nullable: a unit can sit at a servicing warehouse (see `warehouseId`)
	 * instead, or be unplaced during onboarding.
	 */
	@Column('int', {
		name: 'fk_client_address__id',
		unsigned: true,
		nullable: true,
	})
	public clientAddressId!: number | null;

	/**
	 * The servicing warehouse for this merchandiser. Doubles as the placement
	 * for a unit kept at a warehouse rather than at a customer, and as the
	 * geocode fallback for the merchandiser map (PD-12625) when the unit has
	 * no live GPS yet.
	 */
	@Column('int', {
		name: 'fk_warehouse__id',
		unsigned: true,
		nullable: true,
	})
	public warehouseId!: number | null;

	/**
	 * Last-known GPS position of the merchandiser. Merchandisers are stationary
	 * for stretches but DO get relocated; the sensor array unit mounted on the
	 * merchandiser reports GPS as part of its parent telemetry. That position
	 * bubbles up here at ingestion (sensor_device → merchandiser), mirroring how
	 * a truck's GPS bubbles from driver actions onto the VehicleEntity. The
	 * merchandiser map reads these; `currentLocationAt` drives the "updated N min
	 * ago" staleness pill. Null until the unit has reported a position — the map
	 * then falls back to the client-address / warehouse geocode.
	 */
	@Column('decimal', {
		name: 'currentLatitude',
		precision: 10,
		scale: 7,
		nullable: true,
	})
	public currentLatitude!: string | null;

	@Column('decimal', {
		name: 'currentLongitude',
		precision: 10,
		scale: 7,
		nullable: true,
	})
	public currentLongitude!: string | null;

	@Column('datetime', { name: 'currentLocationAt', nullable: true })
	public currentLocationAt!: Date | null;

	/**
	 * What recorded the latest position (e.g. `sensor_device` / `manual`).
	 */
	@Column('varchar', {
		name: 'currentLocationSource',
		length: 32,
		nullable: true,
	})
	public currentLocationSource!: string | null;

	/**
	 * PD-15482 — where this unit was PLACED: the position it first reported
	 * from its assigned customer address, and when.
	 *
	 * Placement is the fact the location alerts turn on. Before a unit is
	 * placed we know nothing about where it should be — a merchandiser is
	 * configured at the warehouse, powered off, and assigned to a customer
	 * days or weeks before it is physically delivered, so its stale warehouse
	 * fix must never read as "in the wrong place". Once it has actually
	 * arrived we have a reference point, and a unit that later leaves it is a
	 * theft worth an alarm.
	 *
	 * The reference is the position the unit REPORTED, not the address
	 * geocode: a customer address geocoded 100m off would otherwise keep a
	 * correctly-placed unit permanently in alarm.
	 */
	@Column('datetime', { name: 'placedAt', nullable: true })
	public placedAt!: Date | null;

	@Column('decimal', {
		name: 'placedLatitude',
		precision: 10,
		scale: 7,
		nullable: true,
	})
	public placedLatitude!: string | null;

	@Column('decimal', {
		name: 'placedLongitude',
		precision: 10,
		scale: 7,
		nullable: true,
	})
	public placedLongitude!: string | null;

	/**
	 * The address the unit was placed at. Kept so a reassignment invalidates
	 * the placement even when it happens outside the app (an import, a direct
	 * write) — the sweep compares this against the current assignment rather
	 * than trusting every writer to have cleared the placement.
	 */
	@Column('int', {
		name: 'fk_client_address__id_placed',
		unsigned: true,
		nullable: true,
	})
	public placedClientAddressId!: number | null;

	/**
	 * PD-15482 — how many CONSECUTIVE position reports have come in more than
	 * the alert radius from the placed position. A single stray fix is normal
	 * GPS noise; the alarm needs two in a row, so the count is kept rather
	 * than alerting on the first.
	 */
	@Column('int', { name: 'awayFromPlacedCount', default: 0 })
	public awayFromPlacedCount!: number;

	/**
	 * The `currentLocationAt` value the placement check last ran against, so a
	 * sweep that sees no NEW report neither re-counts nor clears — the rule is
	 * two consecutive readings, not two consecutive sweeps.
	 */
	@Column('datetime', { name: 'lastLocationCheckAt', nullable: true })
	public lastLocationCheckAt!: Date | null;

	/**
	 * PD-15482 — when the unit was assigned to its current customer address.
	 * A unit assigned but never seen there is undelivered, which is worth
	 * knowing after a few days; loads are staged in advance, so it is normal
	 * for one to sit for a couple of days first.
	 *
	 * NULL means the assignment predates this field (or was made outside the
	 * app): those raise no never-delivered alert, deliberately — starting the
	 * feature by emailing about every historical assignment is exactly the
	 * noise that gets alerts ignored.
	 */
	@Column('datetime', { name: 'clientAddressAssignedAt', nullable: true })
	public clientAddressAssignedAt!: Date | null;

	/**
	 * Last-known temperature of the merchandiser (PD-13938). The sensor array
	 * unit mounted on the merchandiser reports its temperature as part of its
	 * parent telemetry (`sensor_device.device_temperature`); that reading
	 * bubbles up here at ingestion exactly like the GPS fix above. Powers the
	 * High Temp list tab + stat card (an ice unit warming above its safe
	 * range). Null until the attached unit has reported a temperature.
	 */
	@Column('int', { name: 'currentTemperature', nullable: true })
	public currentTemperature!: number | null;

	@Column('datetime', { name: 'currentTemperatureAt', nullable: true })
	public currentTemperatureAt!: Date | null;

	/**
	 * What recorded the latest temperature (e.g. `sensor_device`).
	 */
	@Column('varchar', {
		name: 'currentTemperatureSource',
		length: 32,
		nullable: true,
	})
	public currentTemperatureSource!: string | null;

	@ManyToOne(() => ClientAddressEntity)
	@JoinColumn({ name: 'fk_client_address__id' })
	public fkClientAddress!: ClientAddressEntity | null;

	@ManyToOne(() => WarehouseEntity)
	@JoinColumn({ name: 'fk_warehouse__id' })
	public fkWarehouse!: WarehouseEntity | null;
}
